import { type Dispatch, type SetStateAction, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  CrmExportType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { INTEGRATIONS_MAP } from "../../../../../integrations/constants";
import useLeadExports from "../../../../datahooks/useLeadExports";
import ExportSummary from "../OutreachExportModal/ExportSummary";

import type { CrmExportSummary } from "../../../../types";

interface CrmConfirmationModalProps {
  type: CrmExportType;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  onAsyncJobStarted: (jobId: string, name: string) => void;
}

export default function CrmConfirmationModal({
  type,
  isOpen,
  setIsOpen,
  searchParams,
  onAsyncJobStarted,
}: CrmConfirmationModalProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [summary, setSummary] = useState<CrmExportSummary>();

  const { exportLeadsToCrmIntegration, isExportingLeadsToCrmIntegration } =
    useLeadExports();

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const { title } = INTEGRATIONS_MAP[type];

  function onReset() {
    setSummary(undefined);
  }

  function onConfirm() {
    exportLeadsToCrmIntegration({ searchParams, crmExportType: type })
      .then((result) => {
        if (result.kind === "sync") {
          setSummary(result.summary);
        } else if (result.kind === "async") {
          setIsOpen(false);
          onAsyncJobStarted(result.jobId, title);
        } else {
          toast.success(`Data exported to ${title} successfully`);
          setIsOpen(false);
        }
      })
      .catch(() => {
        toast.error(`Couldn't export data to ${title}`);
        setIsOpen(false);
      });
  }

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={onReset}>
      <Content>
        {summary ? (
          <>
            <Header
              title="Export summary"
              subtitle="A detailed breakdown of your data export"
            />

            <Body>
              <ExportSummary summary={summary} type={type} />

              <Close asChild>
                <Button variant="secondary-black" className="ml-auto">
                  Close
                </Button>
              </Close>
            </Body>
          </>
        ) : (
          <>
            <Header
              title={<>Export leads to {title}?</>}
              subtitle={
                <>
                  Are you sure you want to export your leads to{" "}
                  <span className="font-bold text-black-950">{title}</span>?
                </>
              }
            />

            <Footer className="justify-end">
              <Close asChild>
                <Button variant="secondary-black">Cancel</Button>
              </Close>

              <Button
                onClick={onConfirm}
                isLoading={isExportingLeadsToCrmIntegration}
              >
                Yes, export
              </Button>
            </Footer>
          </>
        )}
      </Content>
    </Component>
  );
}
